/**
 * Settings -> Preferences -> Swipe actions (TRUS-761).
 *
 * What a swipe right / left does on Members and Team conversations, on Mailbox
 * Email, and on a message; how far a swipe must travel; and whether a trackpad
 * swipes at all. Saved per user (and organization) on the server, so the mobile
 * app and every browser use the same choices. Each change saves at once.
 */
import { useState } from 'react';
import { Hand, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { toast } from '@/components/ui/use-toast';
import {
  DEFAULT_SWIPE_SETTINGS,
  saveSwipeSettings,
  useSwipeSettings,
  type ConversationSwipe,
  type EmailSwipe,
  type SwipeSensitivity,
  type SwipeSettings,
} from '@/components/messaging/swipe/swipeSettings';

const selectClass =
  'w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 sm:w-56';

export function SwipeSettingsCard() {
  const { t } = useAppTranslation();
  const settings = useSwipeSettings();
  const [saving, setSaving] = useState(false);

  const save = async (next: SwipeSettings) => {
    setSaving(true);
    try {
      await saveSwipeSettings(next);
    } catch {
      toast({ title: t('settings.swipe.saveFailed', 'Could not save your swipe settings. Try again.'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const conversationOptions: { value: ConversationSwipe; label: string }[] = [
    { value: 'pin', label: t('settings.swipe.actionPin', 'Pin / unpin') },
    { value: 'read', label: t('settings.swipe.actionRead', 'Mark read / unread') },
    { value: 'none', label: t('settings.swipe.actionNone', 'Nothing') },
  ];
  const emailOptions: { value: EmailSwipe; label: string }[] = [
    { value: 'read', label: t('settings.swipe.actionRead', 'Mark read / unread') },
    { value: 'archive', label: t('settings.swipe.actionArchive', 'Archive') },
    { value: 'none', label: t('settings.swipe.actionNone', 'Nothing') },
  ];
  const sensitivityOptions: { value: SwipeSensitivity; label: string; hint: string }[] = [
    { value: 'light', label: t('settings.swipe.light', 'Light'), hint: t('settings.swipe.lightHint', 'A short swipe acts') },
    { value: 'medium', label: t('settings.swipe.medium', 'Medium'), hint: t('settings.swipe.mediumHint', 'About a third of the row') },
    { value: 'firm', label: t('settings.swipe.firm', 'Firm'), hint: t('settings.swipe.firmHint', 'Half the row, fewer accidents') },
  ];

  const row = (id: string, label: string, control: React.ReactNode) => (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between" key={id}>
      <label htmlFor={id} className="text-sm text-gray-700 dark:text-gray-300">
        {label}
      </label>
      {control}
    </div>
  );

  const sideSelect = <T extends string>(
    id: string,
    value: T,
    options: { value: T; label: string }[],
    onChange: (v: T) => void,
  ) => (
    <select id={id} data-testid={id} value={value} disabled={saving} onChange={(e) => onChange(e.target.value as T)} className={selectClass}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );

  return (
    <Card className="card-elevated" data-testid="swipe-settings">
      <CardHeader>
        <CardTitle className="text-heading-4 flex items-center gap-2">
          <Hand className="h-5 w-5" aria-hidden="true" />
          {t('settings.swipe.title', 'Swipe actions')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.swipe.description',
            'Choose what swiping a row does, on a touch screen or with two fingers on a trackpad. Applies here, in the desktop app and in the mobile app.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <section className="space-y-2">
          <h4 className="text-sm font-semibold">{t('settings.swipe.conversations', 'Conversations (Members and Team)')}</h4>
          {row(
            'swipe-conv-right',
            t('settings.swipe.swipeRight', 'Swipe right'),
            sideSelect('swipe-conv-right', settings.conversations.right, conversationOptions, (v) =>
              void save({ ...settings, conversations: { ...settings.conversations, right: v } }),
            ),
          )}
          {row(
            'swipe-conv-left',
            t('settings.swipe.swipeLeft', 'Swipe left'),
            sideSelect('swipe-conv-left', settings.conversations.left, conversationOptions, (v) =>
              void save({ ...settings, conversations: { ...settings.conversations, left: v } }),
            ),
          )}
        </section>

        <section className="space-y-2">
          <h4 className="text-sm font-semibold">{t('settings.swipe.email', 'Mailbox Email')}</h4>
          {row(
            'swipe-email-right',
            t('settings.swipe.swipeRight', 'Swipe right'),
            sideSelect('swipe-email-right', settings.email.right, emailOptions, (v) =>
              void save({ ...settings, email: { ...settings.email, right: v } }),
            ),
          )}
          {row(
            'swipe-email-left',
            t('settings.swipe.swipeLeft', 'Swipe left'),
            sideSelect('swipe-email-left', settings.email.left, emailOptions, (v) =>
              void save({ ...settings, email: { ...settings.email, left: v } }),
            ),
          )}
        </section>

        <section className="space-y-2">
          <h4 className="text-sm font-semibold">{t('settings.swipe.messages', 'Messages')}</h4>
          <label className="flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-300">
            <span>{t('settings.swipe.replyBySwipe', 'Swipe a message right to reply')}</span>
            <input
              type="checkbox"
              data-testid="swipe-message-reply"
              className="h-4 w-4"
              disabled={saving}
              checked={settings.messages.right === 'reply'}
              onChange={(e) => void save({ ...settings, messages: { right: e.target.checked ? 'reply' : 'none' } })}
            />
          </label>
        </section>

        <section className="space-y-2">
          <h4 className="text-sm font-semibold">{t('settings.swipe.sensitivity', 'Sensitivity')}</h4>
          <div role="radiogroup" aria-label={t('settings.swipe.sensitivity', 'Sensitivity')} className="grid grid-cols-3 gap-2">
            {sensitivityOptions.map((o) => {
              const on = settings.sensitivity === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={saving}
                  data-testid={`swipe-sensitivity-${o.value}`}
                  onClick={() => void save({ ...settings, sensitivity: o.value })}
                  className={`rounded-lg border px-3 py-2 text-left text-sm transition ${
                    on
                      ? 'border-v2accent bg-v2accent-soft text-v2accent-ink'
                      : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="block font-semibold">{o.label}</span>
                  <span className="block text-xs opacity-80">{o.hint}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="space-y-2">
          <label className="flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-300">
            <span>
              <span className="block font-semibold">{t('settings.swipe.trackpad', 'Trackpad swipe')}</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {t('settings.swipe.trackpadHint', 'Swipe rows with two fingers on a trackpad (web and desktop app).')}
              </span>
            </span>
            <input
              type="checkbox"
              data-testid="swipe-trackpad"
              className="h-4 w-4"
              disabled={saving}
              checked={settings.trackpad}
              onChange={(e) => void save({ ...settings, trackpad: e.target.checked })}
            />
          </label>
          {settings.trackpad && (
            <label className="flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-300">
              <span>
                <span className="block">{t('settings.swipe.trackpadReverse', 'Reverse trackpad direction')}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.swipe.trackpadReverseHint', 'Turn on if swiping right runs the swipe-left action.')}
                </span>
              </span>
              <input
                type="checkbox"
                data-testid="swipe-trackpad-reverse"
                className="h-4 w-4"
                disabled={saving}
                checked={settings.trackpadReverse}
                onChange={(e) => void save({ ...settings, trackpadReverse: e.target.checked })}
              />
            </label>
          )}
        </section>

        <button
          type="button"
          disabled={saving}
          data-testid="swipe-reset"
          onClick={() => void save(DEFAULT_SWIPE_SETTINGS)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline disabled:opacity-50"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          {t('settings.swipe.reset', 'Reset to defaults')}
        </button>
      </CardContent>
    </Card>
  );
}

export default SwipeSettingsCard;
